/**
 * Kanban-taulun puhdas logiikka (AB-WP6.14): sarakkeiden ja korttien jäsennys,
 * korttien laskenta per sarake ja kortin siirto sarakkeesta toiseen. Ei DOMia —
 * nämä testataan suoraan; runtime.ts:n wireKanban tekee DOM- ja drag-drop-työn.
 */

export interface KanbanColumn {
  id: string;
  name: string;
  color?: string;
}

export interface KanbanCard {
  id: string;
  columnId: string;
  title: string;
  description?: string;
  badges?: string[];
  assignees?: string[];
}

function coerce(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const p = JSON.parse(value);
      if (Array.isArray(p)) return p;
    } catch {
      /* rikkinäinen JSON → tyhjä */
    }
  }
  return [];
}

const strArr = (v: unknown): string[] | undefined =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : undefined;

/**
 * Jäsennä sarakkeet; vaatii string-id:n.
 *
 * <p><b>Otsikko luetaan sekä `name`- että `title`-kentästä.</b> Katalogin
 * oletusarvo käyttää `name`:a mutta `SKILL.md` §7 dokumentoi `title`:n, joten
 * kumpaakin on kirjoitettu — ja koska puuttuva otsikko putoaa `id`:hen, väärä
 * kenttä ei tuottanut virhettä vaan **taulun jossa sarakkeen otsikkona lukee
 * "kuitti-liitetty"**. Vika näkyy vain sille joka katsoo valmista Appia, eli ei
 * sen kirjoittaneelle AI:lle. Molempien lukeminen korjaa myös jo kirjoitetut
 * Appit; `name` voittaa, koska se on katalogin oletus.
 */
export function parseColumns(value: unknown): KanbanColumn[] {
  const cols: KanbanColumn[] = [];
  for (const c of coerce(value)) {
    if (!c || typeof c !== 'object') continue;
    const o = c as Record<string, unknown>;
    if (typeof o.id !== 'string') continue;
    const label = typeof o.name === 'string' ? o.name
                : typeof o.title === 'string' ? o.title
                : o.id;
    cols.push({
      id: o.id,
      name: label,
      color: typeof o.color === 'string' ? o.color : undefined,
    });
  }
  return cols;
}

/** Jäsennä kortit; vaatii string-id:n ja -columnId:n. */
export function parseCards(value: unknown): KanbanCard[] {
  const cards: KanbanCard[] = [];
  for (const c of coerce(value)) {
    if (!c || typeof c !== 'object') continue;
    const o = c as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.columnId !== 'string') continue;
    cards.push({
      id: o.id,
      columnId: o.columnId,
      title: typeof o.title === 'string' ? o.title : '',
      description: typeof o.description === 'string' ? o.description : undefined,
      badges: strArr(o.badges),
      assignees: strArr(o.assignees),
    });
  }
  return cards;
}

/** Kortit sarakkeessa (alkuperäisessä järjestyksessä). */
export function cardsInColumn(cards: KanbanCard[], columnId: string): KanbanCard[] {
  return cards.filter((c) => c.columnId === columnId);
}

/** Kortti-määrä per sarake-id (kaikki sarakkeet mukaan, myös tyhjät). */
export function columnCounts(cards: KanbanCard[], columns: KanbanColumn[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const col of columns) counts[col.id] = 0;
  for (const card of cards) {
    counts[card.columnId] = (counts[card.columnId] || 0) + 1;
  }
  return counts;
}

/** Siirrä kortti toiseen sarakkeeseen (uusi taulukko); tuntematon id → muuttumaton. */
export function moveCard(cards: KanbanCard[], cardId: string, toColumnId: string): KanbanCard[] {
  return cards.map((c) => (c.id === cardId ? { ...c, columnId: toColumnId } : c));
}
